/**
 * Shared Server-Sent Events stream consumer (PRD §5.5 U-02).
 *
 * One EventSource per browser tab — multiple components subscribe via
 * the returned hook value. The Ops Center (Day 9) needs Map + Schematic
 * + Waterfall + AlertFeed + KpiBar to all react to the same SSE events
 * without each opening its own connection (which would multiply load on
 * the API + risk hitting browser per-host EventSource limits).
 *
 * Implementation: a module-level singleton EventSource lazily opened on
 * first hook subscription, refcounted on the way down. When all callers
 * unmount, the connection closes after a short grace period.
 *
 * Caller usage:
 *
 *   const { lastEvent, connected } = useAlertStream({ siteId });
 *
 * `lastEvent` is the most recent SSE message of any type. The
 * EventSource auto-reconnects on network blips (built into the spec).
 *
 * Design notes:
 *   - The hook does NOT decode payloads — it returns the raw `MessageEvent`.
 *     Callers parse `event.data` themselves. This keeps the hook generic
 *     and lets each consumer trigger different react-query invalidations.
 *   - We intentionally don't expose every received event as an array —
 *     the hook's purpose is "react to NEW alerts", not "build a feed".
 *     Components that want a feed re-fetch via the existing
 *     `/api/v1/incidents` endpoint after a `new_incident` event lands.
 */

import { useEffect, useState } from "react";

const SSE_PATH = "/api/v1/sse/alerts";

interface AlertStreamSnapshot {
  /** Most recent message of any SSE type. null until the first message. */
  lastEvent: { type: string; data: unknown; receivedAt: number } | null;
  /** EventSource readyState mapped to a boolean. */
  connected: boolean;
  /** Counter that increments on every received event — useful as
   *  a useEffect dependency to trigger downstream re-fetches. */
  eventCount: number;
}

interface SubscriberCallback {
  (snapshot: AlertStreamSnapshot): void;
}

// ── Module singleton ───────────────────────────────────────────────────

let source: EventSource | null = null;
let subscribers = new Set<SubscriberCallback>();
let snapshot: AlertStreamSnapshot = {
  lastEvent: null,
  connected: false,
  eventCount: 0,
};
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  // Snapshot is a fresh object so React's reference-equality re-render check fires.
  const next = { ...snapshot };
  for (const cb of subscribers) cb(next);
}

function ensureConnection(siteId: string | null | undefined): void {
  // KNOWN LIMITATION (deferred to multi-site Phase 2.1, see BUG_LOG):
  // when an existing source is bound to siteId A and a new subscriber
  // arrives with siteId B, we reuse the source bound to A. This is
  // intentional in v1.5 because PGU_SOUTHERN_BRANCH is the only site,
  // and reopening the connection at every cross-page nav was the worse
  // failure mode. Revisit when introducing per-page site filtering.
  if (source) return;
  const qs = siteId ? `?site_id=${encodeURIComponent(siteId)}` : "";
  const es = new EventSource(`${SSE_PATH}${qs}`, { withCredentials: true });
  source = es;

  es.addEventListener("open", () => {
    snapshot = { ...snapshot, connected: true };
    notify();
  });

  es.addEventListener("error", () => {
    // EventSource auto-reconnects; we just mark stale.
    snapshot = { ...snapshot, connected: false };
    notify();
  });

  // Wildcard handler — listen on each known type explicitly because
  // EventSource doesn't have a "*" wildcard. The server emits these
  // four (sse-broker.ts SseEventType union).
  for (const type of [
    "new_incident",
    "incident_updated",
    "incident_resolved",
    "keepalive",
  ]) {
    es.addEventListener(type, (ev: MessageEvent) => {
      let data: unknown = ev.data;
      try {
        data = JSON.parse(ev.data as string);
      } catch {
        /* leave as raw string */
      }
      snapshot = {
        lastEvent: { type, data, receivedAt: Date.now() },
        connected: true,
        eventCount: snapshot.eventCount + 1,
      };
      notify();
    });
  }
}

function tearDown(): void {
  if (source) {
    source.close();
    source = null;
  }
  snapshot = { lastEvent: null, connected: false, eventCount: 0 };
}

export interface UseAlertStreamOptions {
  /** Optional site filter — passed as ?site_id=X on the SSE URL. */
  siteId?: string | null;
}

export function useAlertStream(
  opts: UseAlertStreamOptions = {},
): AlertStreamSnapshot {
  const [local, setLocal] = useState<AlertStreamSnapshot>(snapshot);

  useEffect(() => {
    // Cancel a pending teardown if a new subscriber arrives in the grace window
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    ensureConnection(opts.siteId ?? null);
    const cb: SubscriberCallback = (next) => setLocal(next);
    subscribers.add(cb);
    // Hand the caller our latest known state immediately
    setLocal(snapshot);
    return () => {
      subscribers.delete(cb);
      // Defer connection teardown by 5 s so route changes (which unmount
      // and remount in quick succession) don't churn the connection.
      if (subscribers.size === 0 && !closeTimer) {
        closeTimer = setTimeout(() => {
          if (subscribers.size === 0) tearDown();
          closeTimer = null;
        }, 5000);
      }
    };
    // siteId change reopens — important: changing site filter must close
    // and reopen with the new query string.
  }, [opts.siteId]);

  return local;
}

/** Test/inspection helper. */
export function _alertStreamSubscribersForTest(): number {
  return subscribers.size;
}
